import { Tag } from '../../pages/tagsearch/[tagIds]';
import { createTagNameIdObj, findTagsByNames } from '../../server/libs/tag';
import {
  BannerSettings,
  MobileSpecialBar,
  Special,
} from '../../server/libs/siteSettings';
import { isDoctorRecruiterAccount } from './user';

/**
 * `SPECIALS`オブジェクトからタグ名がマッチする最初の`Special`(特集)オブジェクトを取得する。
 */
export function findSpecialByTags(
  articleTags: Tag[],
  isLicenseVerifiedDoctor?: boolean,
  articleId?: string,
  bannerSettings?: BannerSettings,
): Special | undefined {
  const matchingKey = bannerSettings?.bannerKeys?.find((key) => {
    const setting = bannerSettings?.banners?.[key];

    // 表示制御設定に基づいて表示可否を判断する
    if (setting?.visibility?.isVerifiedDoctorOnly && !isLicenseVerifiedDoctor) {
      return false;
    }

    if (articleId && setting?.excludeArticleIds?.includes(articleId)) {
      return false;
    }

    const bySingleTags: boolean =
      setting?.tags.some((tagName) =>
        articleTags.some((t) => t.name === tagName),
      ) || false;
    // andTags設定値がある場合は、一セットのtag配列に含まれる全てのタグがarticleTagsに含まれているかを確認する
    const byAndTags: boolean = setting?.andTags
      ? setting.andTags.some((tags) =>
          tags.every((tagName) => articleTags.some((t) => t.name === tagName)),
        )
      : false;

    return bySingleTags || byAndTags;
  }) as string | undefined;
  return matchingKey ? bannerSettings?.banners?.[matchingKey] : undefined;
}

/**
 * MOBILE_SPECIAL_BARS 配列からモバイルで下部にバナーを固定表示する設定値を取得する。
 */
export function findMobileSpecialBarByTags(
  tags: Tag[],
  isLicenseVerifiedDoctor: boolean,
  articleId?: string,
  bannerSettings?: BannerSettings,
): MobileSpecialBar | undefined {
  return bannerSettings?.mobileSpecialBars?.find((special) => {
    // 表示制御設定に基づいて表示可否を判断する
    if (special?.visibility?.isVerifiedDoctorOnly && !isLicenseVerifiedDoctor) {
      return false;
    }

    if (articleId && special?.excludeArticleIds?.includes(articleId)) {
      return false;
    }

    return special.tags.some((tag) => tags.some((t) => t.name === tag));
  });
}

/**
 * articleIdsとマッチするSpecialを取得する
 */
function findSpecialByArticleId<T extends string>(
  articleId: string,
  isLicenseVerifiedDoctor: boolean,
  keys: readonly T[],
  source: Record<T, Special>,
): Special | undefined {
  return keys
    .map((key) => source[key])
    .find((setting) => {
      // 表示制御設定に基づいて表示可否を判断する
      if (
        setting?.visibility?.isVerifiedDoctorOnly &&
        !isLicenseVerifiedDoctor
      ) {
        return false;
      }

      return (
        Array.isArray(setting?.articleIds) &&
        setting.articleIds.includes(articleId)
      );
    });
}

/**
 *  articleIds配列を持つARTICLE_ID_BANNER_FORMERから引数のarticleIdとマッチする最初の要素を返す
 */
export function findSpecialByArticleIdFormer(
  articleId: string,
  isLicenseVerifiedDoctor: boolean,
  bannerSettings?: BannerSettings,
): Special | undefined {
  return findSpecialByArticleId(
    articleId,
    isLicenseVerifiedDoctor,
    bannerSettings?.articleIdBannerKeysFormer || [],
    bannerSettings?.articleIdBannerFormer || {},
  ) as Special | undefined;
}

/**
 *  articleIds配列を持つSPECIALSから引数のarticleIdとマッチする最初の要素を返す
 */
export function findSpecialByArticleIdLatter(
  articleId: string,
  isLicenseVerifiedDoctor: boolean,
  bannerSettings?: BannerSettings,
): Special | undefined {
  return findSpecialByArticleId(
    articleId,
    isLicenseVerifiedDoctor,
    bannerSettings?.bannerKeys || [],
    bannerSettings?.banners || {},
  ) as Special | undefined;
}

/**
 * articleIdsとマッチするMobileSpecialBarを取得する
 */
function findMobileSpecialBarByArticleId(
  articleId: string,
  isLicenseVerifiedDoctor: boolean,
  specials: MobileSpecialBar[],
): MobileSpecialBar | undefined {
  const matchingKey = specials.find((special) => {
    if (!special.articleIds || !special.articleIds.length) {
      return false;
    }

    // 表示制御設定に基づいて表示可否を判断する
    if (special?.visibility?.isVerifiedDoctorOnly && !isLicenseVerifiedDoctor) {
      return false;
    }

    return special.articleIds.some((id: string) => id === articleId);
  }) as MobileSpecialBar | undefined;

  if (!matchingKey) {
    return undefined;
  }
  return matchingKey;
}

/**
 * MOBILE_ARTICLE_ID_BANNER_FORMERからarticleIdsとマッチするMobileSpecialBarを取得する
 */
export function findMobileSpecialBarByArticleIdFormer(
  articleId: string,
  isLicenseVerifiedDoctor: boolean,
  bannerSettings?: BannerSettings,
): MobileSpecialBar | undefined {
  return findMobileSpecialBarByArticleId(
    articleId,
    isLicenseVerifiedDoctor,
    bannerSettings?.mobileArticleIdBannerFormer || [],
  );
}

/**
 * MOBILE_SPECIAL_BARSからarticleIdsとマッチするMobileSpecialBarを取得する
 */
export function findMobileSpecialBarByArticleIdLatter(
  articleId: string,
  isLicenseVerifiedDoctor: boolean,
  bannerSettings?: BannerSettings,
): MobileSpecialBar | undefined {
  return findMobileSpecialBarByArticleId(
    articleId,
    isLicenseVerifiedDoctor,
    bannerSettings?.mobileSpecialBars || [],
  );
}

/**
 * tagTexts, andTagTextsに設定されたタグに対応するTagをそれぞれ取得する
 *
 * @param tagTexts // いずれかの一つのタグが含まれている記事を取得する設定の配列
 * @param andTagTexts // 二つ以上のタグが必ず含まれている記事を取得する設定の配列
 */
export async function getTagsWithTagSettings({
  tagTexts,
  andTagTexts,
}: {
  tagTexts: string[];
  andTagTexts?: string[][];
}): Promise<{
  tagsFromTagTexts: Tag[];
  tagsFromAndTagTexts: Tag[][];
}> {
  // tagIdを取得するクエリを一回にするためにタグ名をflatな配列にまとめ重複を除去
  const allTagTexts: string[] = Array.from(
    new Set([...tagTexts, ...(andTagTexts?.flat() ?? [])]),
  );
  // articleIdを取得するために必要なtagIdの配列を取得
  const tagRecordsFromTagTexts = await findTagsByNames(allTagTexts);
  // タグ名をキー、値がtagIdのオブジェクトを生成
  const tagNameIdObj = createTagNameIdObj(tagRecordsFromTagTexts);

  // tagTextsをTagの配列に変換
  const tagsFromTagTexts: Tag[] = tagTexts
    .map((tagText) => {
      const tagId = tagNameIdObj[tagText];
      return tagId
        ? {
            id: tagId,
            name: tagText,
          }
        : null;
    })
    .filter(
      (v) => !!v, // 存在しないタグは除外
    ) as Tag[];

  // andTagTextsをtagIdの配列の配列に変換
  const tagsFromAndTagTexts: Tag[][] = (andTagTexts ?? [])
    .map((tagTextArr, andTagTextIndex) =>
      tagTextArr.map((tagText) => {
        const tagId = tagNameIdObj[tagText];
        return tagId
          ? {
              id: tagId,
              name: tagText,
            }
          : null;
      }),
    )
    // 一つの組み合わせに対して全てのタグが存在する場合のみ有効な設定値とする
    .filter((v) => v.every((tag) => !!tag)) as Tag[][];

  return {
    tagsFromTagTexts,
    tagsFromAndTagTexts,
  };
}

export function shouldShowSpecialPage(
  articleId: string | undefined,
  isCorporationAccount: boolean,
  isPublisherAccount: boolean,
  isDoctorRecruiterAccount: boolean,
  isDemoAccount: boolean,
): boolean {
  if (isDemoAccount) {
    return false;
  }
  if (isCorporationAccount) {
    const publisherAllowedArticleIds = [
      'home-medical-care',
      'resident-internal-medicine',
      'designated-intractable-diseases-and-rare-diseases',
      'resident-handbook-campaign',
    ];
    if (
      isPublisherAccount &&
      publisherAllowedArticleIds.includes(articleId ?? '')
    ) {
      return true;
    }
    const doctorRecruiterAllowedArticleIds = [
      'home-medical-care',
      'resident-handbook-campaign',
      'resident-internal-medicine',
    ];
    if (
      isDoctorRecruiterAccount &&
      doctorRecruiterAllowedArticleIds.includes(articleId ?? '')
    ) {
      return true;
    }
    return false;
  }

  return true;
}
